import {ApolloQueryResult} from '@apollo/client';
import React, {useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT,
  CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import useDraftState from 'ui-library/hooks/useDraftState';
import * as Keyboard from 'util/Keyboard';
import strUtil from 'util/strUtil';

import {createLibraryParameterDefinition} from '../network/LibraryParameterDefinitionDAO';
import {LibraryParameterDefinition, LibraryParameterDefinitionDataType} from '../ParameterLibraryTypes';
import {ParameterLibraryListingQuery} from './__generated/ParameterLibraryListingQuery';
import {getParameterNameErrorMessage, getDescriptionErrorMessage} from './parameterLibraryListingUtils';

interface DraftLibraryParameterDefinition {
  name: string;
  dataType: LibraryParameterDefinitionDataType;
  description: string;
  unitTypeId: string | null;
}

const EMPTY_PARAMETER_DEFINITION: DraftLibraryParameterDefinition = {
  name: '',
  dataType: FieldDefinitionType.FLOAT,
  description: '',
  unitTypeId: null,
};

export interface CreateParameterDefinitionResult {
  draftParameterDefinition: DraftLibraryParameterDefinition;
  updateDraftParameterDefinition: (updates: Partial<DraftLibraryParameterDefinition>) => void;
  isEditing: boolean;
  startEditing: () => void;
  isSaving: boolean;
  isValid: boolean;
  nameErrorMessage: string | null;
  descriptionErrorMessage: string | null;
  unitTypeErrorMessage: string | null;
  onAddParameterDefinition: () => Promise<void>;
  handleNewRowKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
  onCancel: () => void;
}

export function useCreateParameterDefinition({
  parameterDefinitions,
  refetch,
  markParameterDefinitionRowsForAttention,
}: {
  parameterDefinitions: Array<LibraryParameterDefinition>;
  refetch: () => Promise<ApolloQueryResult<ParameterLibraryListingQuery>>;
  markParameterDefinitionRowsForAttention: (ids: Array<string>) => void;
}): CreateParameterDefinitionResult {
  const [isSaving, setIsSaving] = useState(false);
  const draftState = useDraftState<DraftLibraryParameterDefinition>(EMPTY_PARAMETER_DEFINITION);
  const {
    data: draftParameterDefinition,
    updateDraftData: updateDraftParameterDefinition,
    isEditing,
    startEditing,
    stopEditing,
  } = draftState;

  const existingNames = useMemo(
    () => new Set(parameterDefinitions.map((pd) => pd.name)),
    [parameterDefinitions]
  );

  const trimmedDraftParameterDefinitionName = draftParameterDefinition.name.trim();
  const nameErrorMessage = getParameterNameErrorMessage(trimmedDraftParameterDefinitionName, existingNames);
  const descriptionErrorMessage = getDescriptionErrorMessage(draftParameterDefinition.description);
  const unitTypeErrorMessage =
    draftParameterDefinition.dataType === FieldDefinitionType.FLOAT &&
    draftParameterDefinition.unitTypeId == null
      ? 'Unit type is required for decimal-type parameters.'
      : null;
  const isValid =
    !strUtil.isEmpty(draftParameterDefinition.name) &&
    nameErrorMessage == null &&
    descriptionErrorMessage == null &&
    unitTypeErrorMessage == null;

  function onCancel(): void {
    if (isSaving) {
      return;
    }
    stopEditing();
    updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
  }

  async function onAddParameterDefinition(): Promise<void> {
    if (isSaving || !isValid) {
      return;
    }
    const profileEventId = uuidv4();
    logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT, id: profileEventId});
    const parameterDefinitionToCreate: DraftLibraryParameterDefinition = {
      ...draftParameterDefinition,
      name: trimmedDraftParameterDefinitionName,
    };
    setIsSaving(true);
    try {
      const createResult = await createLibraryParameterDefinition(parameterDefinitionToCreate);
      stopEditing();
      await refetch();
      markParameterDefinitionRowsForAttention(createResult.createdIds);
      updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
      notifications.success(
        <>
          Parameter <strong>{trimmedDraftParameterDefinitionName}</strong> added
        </>
      );
      logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT, id: profileEventId});
    } finally {
      setIsSaving(false);
    }
  }

  async function handleNewRowKeyDown(e: React.KeyboardEvent<HTMLInputElement>): Promise<void> {
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e) && isValid && !isSaving) {
      e.preventDefault();
      await onAddParameterDefinition();
    }
  }

  return {
    draftParameterDefinition,
    updateDraftParameterDefinition,
    isEditing,
    startEditing,
    isSaving,
    isValid,
    nameErrorMessage,
    descriptionErrorMessage,
    unitTypeErrorMessage,
    onAddParameterDefinition,
    handleNewRowKeyDown,
    onCancel,
  };
}
